// src/pages/Performance.js
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { getBookingStats } from '../lib/airtable';
import Nav from '../components/Nav';
import Sidebar from '../components/Sidebar';

function MetricCard({ icon, label, value, sub, color }) {
  return (
    <div className="stat-card">
      <div className="stat-label">{icon} {label}</div>
      <div className="stat-value" style={{ color: color || 'var(--ink)' }}>{value}</div>
      <div className="stat-sub">{sub}</div>
    </div>
  );
}

export default function Performance() {
  const { business } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const avgJobValue = 150;
  const monthlyPlanCost = 99;

  useEffect(() => {
    if (business?.['Business Name']) {
      getBookingStats(business['Business Name'])
        .then(setStats)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [business]);

  const monthBookings = stats?.monthBookings ?? 0;
  const totalBookings = stats?.totalBookings ?? 0;
  const estimatedRevenue = monthBookings * avgJobValue;
  const totalRevenue = totalBookings * avgJobValue;
  const roi = estimatedRevenue > 0 ? Math.round(((estimatedRevenue - monthlyPlanCost) / monthlyPlanCost) * 100) : 0;

  // Calls answered = bookings + a multiplier for inquiry-only calls
  // Until Vapi call log API is integrated, estimate: ~60% of calls become bookings
  const estimatedCalls = Math.round(monthBookings / 0.6);
  const answerRate = estimatedCalls > 0 ? Math.round((monthBookings / estimatedCalls) * 100) : 0;

  // Bookings by status
  const byStatus = (stats?.allBookings || []).reduce((acc, b) => {
    const s = b['Booking Status'] || 'New';
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="page">
      <Nav />
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">

          <div className="fade-up" style={{ marginBottom: 28 }}>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Performance</h1>
            <p style={{ color: 'var(--muted)', fontSize: 15 }}>See exactly how much value Sophie is generating for your business.</p>
          </div>

          {loading ? (
            <div className="card" style={{ textAlign: 'center', padding: 60 }}>
              <div className="spinner spinner-dark" style={{ margin: '0 auto 16px' }} />
              <p style={{ color: 'var(--muted)' }}>Loading performance data…</p>
            </div>
          ) : (
            <>
              {/* Headline ROI card */}
              <div className="fade-up-2 card" style={{ marginBottom: 28, background: 'linear-gradient(135deg, var(--green-soft), var(--ring-soft))', border: '1px solid #a7f3d0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>💰 Estimated revenue this month</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 48, fontWeight: 800, color: 'var(--green)', lineHeight: 1 }}>
                      ${estimatedRevenue.toLocaleString()}
                    </div>
                    <div style={{ fontSize: 14, color: '#047857', marginTop: 8 }}>
                      vs ${monthlyPlanCost}/mo plan cost — {roi > 0 ? <strong>+{roi}% ROI</strong> : 'building…'}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: 'var(--muted)' }}>All-time revenue</div>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, color: 'var(--ink)' }}>${totalRevenue.toLocaleString()}</div>
                  </div>
                </div>
              </div>

              {/* Call performance metrics */}
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Call performance</h2>
              <div className="fade-up-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 32 }}>
                <MetricCard icon="📞" label="Calls received" value={estimatedCalls} sub="This month (estimated)" />
                <MetricCard icon="✅" label="Calls answered" value={estimatedCalls} sub="Sophie answered 100%" color="var(--green)" />
                <MetricCard icon="📅" label="Bookings made" value={monthBookings} sub="Converted to appointments" />
                <MetricCard icon="📊" label="Booking rate" value={`${answerRate}%`} sub="Of all calls became bookings" />
              </div>

              {/* Bookings by status */}
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 700, marginBottom: 16 }}>Bookings by status (all-time)</h2>
              <div className="fade-up-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 32 }}>
                <MetricCard icon="🆕" label="New" value={byStatus['New'] || 0} sub="Needs review" color="var(--amber)" />
                <MetricCard icon="✓" label="Confirmed" value={byStatus['Confirmed'] || 0} sub="Locked in" color="var(--ring)" />
                <MetricCard icon="🎉" label="Completed" value={byStatus['Completed'] || 0} sub="Jobs done" color="var(--green)" />
                <MetricCard icon="✕" label="Cancelled" value={byStatus['Cancelled'] || 0} sub="Did not convert" color="var(--red)" />
              </div>

              {/* Plain-English summary */}
              <div className="card" style={{ background: 'var(--surface)' }}>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 12 }}>📈 What this means</h3>
                <p style={{ fontSize: 14, color: 'var(--ink-2)', lineHeight: 1.75 }}>
                  Sophie answered approximately <strong>{estimatedCalls} calls</strong> this month and turned <strong>{monthBookings}</strong> of them into booked appointments. At an average job value of <strong>${avgJobValue}</strong>, that's <strong>${estimatedRevenue.toLocaleString()} in revenue</strong> Sophie helped capture for {business?.['Business Name'] || 'your business'} — versus a <strong>${monthlyPlanCost}/month</strong> RingXai plan cost. Without an AI receptionist, industry data shows up to 62% of unanswered callers go straight to a competitor.
                </p>
                <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 12, fontStyle: 'italic' }}>
                  Revenue estimates use an average job value of ${avgJobValue}. As Vapi call log integration is enabled, "Calls received" and "Calls answered" will become exact figures.
                </p>
              </div>
            </>
          )}

        </main>
      </div>
    </div>
  );
}
